import { useEffect, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import type { ArtifactMeta, Citation, DeepAnalysisJob } from "@backstory/protocol";
import { CopyButton, Dialog, Hint, useToast } from "../../../components/ui";
import { formatClock } from "../../../lib/format";
import { useFeedback, type AiCapabilityState } from "../hooks";
import { rejectedMessage } from "../api";

/**
 * The mark an AI control wears. Panels announce themselves with the `AI` badge,
 * but a badge inside a button competes with its label, so buttons that spend
 * model budget carry this instead.
 */
export function AiSparkle({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M7 1l1.2 3.8L12 6 8.2 7.2 7 11 5.8 7.2 2 6l3.8-1.2z" />
      <path d="M12.5 9.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
    </svg>
  );
}

/** One visual contract for anything authored by AI. */
export function AiOutput({
  children,
  className,
  testId,
  compact = false,
  mark = "AI generated",
}: {
  children: ReactNode;
  className?: string;
  testId?: string;
  compact?: boolean;
  mark?: string;
}) {
  return (
    <div className={`ai-output${compact ? " compact" : ""}${className ? ` ${className}` : ""}`} data-testid={testId}>
      <div className="ai-output-mark">
        <AiSparkle size={12} />
        <span>{mark}</span>
      </div>
      <div className="ai-output-body">{children}</div>
    </div>
  );
}

/**
 * Why an AI control is dead, on hover or tap. Only while it is dead: once AI
 * works the control is left bare, so its own tooltip describes the action.
 */
export function AiBlockedTooltip({ ai, children }: { ai: AiCapabilityState; children: ReactNode }) {
  if (!ai.blocked) return <>{children}</>;
  return (
    <Hint
      text={
        <>
          <strong>{ai.hint}.</strong>
          {ai.detail}
        </>
      }
    >
      {children}
    </Hint>
  );
}

/** The same reason as a sentence, where a whole panel has nothing to show. */
export function AiUnavailableNote({ ai }: { ai: AiCapabilityState }) {
  if (!ai.blocked) return null;
  return (
    <p className="muted small ai-unavailable" role="note">
      {ai.hint}.
    </p>
  );
}

export interface CitationTools {
  seekTo: (c: Citation) => void;
  label: (c: Citation, fmt: (ms: number) => string) => string;
}

/** Citation chips that seek the Player. Renders nothing when there are no citations. */
export function CitationChips({ citations, tools, sessionHref }: { citations: Citation[] | undefined; tools?: CitationTools; sessionHref?: (c: Citation & { sessionId?: string }) => { to: string; params: Record<string, string>; search?: Record<string, unknown> } | null }) {
  if (!citations?.length) return null;
  return (
    <span className="cite-row" aria-label="Evidence">
      {citations.map((c, i) => {
        const label = tools ? tools.label(c, formatClock) : c.stepId;
        const href = sessionHref?.(c);
        if (href) {
          return (
            <Link key={`${c.stepId}-${i}`} className="cite-chip" to={href.to as never} params={href.params as never} search={(href.search ?? {}) as never} title={`Evidence ${c.stepId}`}>
              {label}
            </Link>
          );
        }
        return (
          <button key={`${c.stepId}-${i}`} type="button" className="cite-chip" onClick={() => tools?.seekTo(c)} aria-disabled={!tools} title={`Jump to evidence ${c.stepId}`} data-testid="cite-chip">
            {label}
          </button>
        );
      })}
    </span>
  );
}

/** A sentence with its evidence. */
export function Claim({ label, text, citations, tools, confidence, evidenceFirst = false }: { label?: string; text: string; citations?: Citation[]; tools?: CitationTools; confidence?: number; evidenceFirst?: boolean }) {
  if (evidenceFirst) {
    return (
      <p className="claim evidence-first">
        <CitationChips citations={citations} tools={tools} />
        <span className="claim-copy">
          {label && <strong>{label}</strong>}
          {text}
          {confidence !== undefined && <span className="confidence"> · confidence {Math.round(confidence * 100)}%</span>}
        </span>
      </p>
    );
  }
  return (
    <p className="claim">
      {label && <strong>{label}</strong>}
      {text}
      <CitationChips citations={citations} tools={tools} />
      {confidence !== undefined && <span className="confidence"> · confidence {Math.round(confidence * 100)}%</span>}
    </p>
  );
}

export function ArtifactMetaLine({ meta, children }: { meta: ArtifactMeta; children?: ReactNode }) {
  return (
    <div className="ai-meta">
      <span>Generated {new Date(meta.generatedAt).toLocaleString()}</span>
      {children}
      <Feedback meta={meta} />
    </div>
  );
}

export function Feedback({ meta }: { meta: ArtifactMeta }) {
  const fb = useFeedback();
  const toast = useToast();
  const [local, setLocal] = useState<"up" | "down" | undefined>(meta.feedback);
  const [askingWhy, setAskingWhy] = useState(false);
  const [reason, setReason] = useState<"wrong_facts" | "missed_issue" | "not_useful">();
  useEffect(() => setLocal(meta.feedback), [meta.id, meta.feedback]);

  const save = (rating: "up" | "down" | "", why?: string) => {
    const previous = local;
    setLocal(rating || undefined);
    fb.mutate(
      { meta, rating, reason: why },
      {
        onError: (error) => {
          setLocal(previous);
          toast.push("error", (error as Error).message);
        },
      },
    );
  };

  const rate = (rating: "up" | "down") => {
    if (local === rating) {
      save("");
      return;
    }
    if (rating === "down") {
      setReason(undefined);
      setAskingWhy(true);
      return;
    }
    save("up");
  };

  return (
    <>
      <span className="feedback" role="group" aria-label="Was this helpful?">
        <button type="button" aria-pressed={local === "up"} onClick={() => rate("up")} aria-label="Helpful" title="Helpful" disabled={fb.isPending}>
          👍
        </button>
        <button type="button" aria-pressed={local === "down"} onClick={() => rate("down")} aria-label="Not helpful" title="Not helpful" disabled={fb.isPending}>
          👎
        </button>
      </span>
      <Dialog
        open={askingWhy}
        onClose={() => setAskingWhy(false)}
        title="What was wrong?"
        width={420}
        footer={
          <>
            <button type="button" className="btn ghost" onClick={() => setAskingWhy(false)}>Cancel</button>
            <button
              type="button"
              className="btn primary"
              disabled={!reason}
              onClick={() => {
                if (!reason) return;
                setAskingWhy(false);
                save("down", reason);
              }}
            >
              Send feedback
            </button>
          </>
        }
      >
        <p className="muted small">Choose the closest reason. This helps Backstory review AI quality; it does not change this result.</p>
        <div className="feedback-reasons" role="radiogroup" aria-label="Reason">
          {[
            ["wrong_facts", "It contains wrong facts"],
            ["missed_issue", "It missed the issue"],
            ["not_useful", "It was not useful"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className="btn"
              role="radio"
              aria-checked={reason === value}
              onClick={() => setReason(value as typeof reason)}
            >
              {label}
            </button>
          ))}
        </div>
      </Dialog>
    </>
  );
}

export function JobStatus({ job, polls }: { job?: DeepAnalysisJob; polls?: number }) {
  const label = job?.status === "running" ? "Analyzing the replay, traces and errors" : job?.status === "queued" ? "Queued" : "Starting";
  return (
    <div className="job-status" role="status" aria-live="polite" data-testid="job-status">
      <span className="dot" aria-hidden="true" />
      <span>
        {label}
        {polls ? ` (${polls * 1.5 | 0}s)` : ""}…
      </span>
    </div>
  );
}

export function Rejected({ job, orgId }: { job: DeepAnalysisJob; orgId?: string }) {
  const m = rejectedMessage(job.rejectedReason);
  return (
    <div className="rejected" role="alert" data-testid="rejected">
      <h4>{m.title}</h4>
      <p className="small" style={{ margin: 0 }}>{m.body}</p>
      {m.action && orgId && (
        <p style={{ margin: "8px 0 0" }}>
          {m.action === "usage" ? (
            <Link to="/orgs/$org/ai/usage" params={{ org: orgId }} className="btn ghost">View AI usage</Link>
          ) : (
            <Link to="/orgs/$org/ai" params={{ org: orgId }} className="btn ghost">Open AI settings</Link>
          )}
        </p>
      )}
    </div>
  );
}

export function CodeBlock({ code, filename, language = "text", onDownload }: { code: string; filename?: string; language?: string; onDownload?: () => void }) {
  return (
    <div>
      <div className="code-block-actions">
        {filename && <span className="mono small muted" style={{ marginRight: "auto" }}>{filename}</span>}
        <CopyButton value={code} />
        {onDownload && <button type="button" className="btn ghost" onClick={onDownload} data-testid="download">Download</button>}
      </div>
      <pre className="code-block" data-language={language}>
        <code>{code}</code>
      </pre>
    </div>
  );
}

/** Minimal unified-diff coloring. */
export function DiffView({ diff }: { diff: string }) {
  return (
    <pre className="code-block diff-view" aria-label="Diff">
      {diff.split("\n").map((line, i) => {
        const cls = line.startsWith("+") && !line.startsWith("+++") ? "add" : line.startsWith("-") && !line.startsWith("---") ? "del" : line.startsWith("@@") ? "hunk" : "";
        return (
          <span key={i} className={cls}>
            {line}
            {"\n"}
          </span>
        );
      })}
    </pre>
  );
}
